import { Ripples, StopIcon, Tooltip, useKeyPressedHighlight } from '@sodra/bongo-ui'
import { useMedia } from '@sodra/use-media'
import { Block, InlineBlock } from 'jsxstyle/preact'
import { h } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'

type RecordButtonProps = {
  name?: string
  disabled?: boolean
  loading?: boolean
  tooltipText?: string
  state?: string
  onChange: (state: string) => void
  onFocus?: () => void
  onBlur?: () => void
}

export const RecordButton = ({
  name = 'button',
  disabled,
  loading,
  tooltipText,
  state = 'idle',
  onChange,
  onFocus,
  onBlur
}: RecordButtonProps) => {
  const iconButton = useRef()

  useEffect(() => {
    if (disabled) {
      setFocus(false)
      setHover(false)
    }
  }, [disabled])

  const borderRadius = '50%'
  const color = 'var(--on-surface)'
  const highlightColor = '#fff'

  const [focus, setFocus] = useState<boolean>(false)
  const [hover, setHover] = useState<boolean>(false)
  const [keyPressedHighlights, addKeyPressedHighlight] = useKeyPressedHighlight({
    color: highlightColor,
    borderRadius
  })

  const supportsHover = useMedia(['(hover: hover)'], [true], false)
  const clickable = !disabled && !loading

  const toggleState = () => {
    if (state === 'idle') {
      onChange('recording')
    }
    if (state === 'recording') {
      onChange('idle')
    }
  }

  const handleClick = (e: any) => {
    e.stopPropagation()
    toggleState()
    setFocus(false)
  }

  const handleKeyDown = (e: any) => {
    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault()
      e.stopPropagation()
      addKeyPressedHighlight()
      toggleState()
    }
  }

  const handleMouseOver = () => setHover(true)

  const handleMouseOut = () => setHover(false)

  const handleFocus = () => {
    setFocus(true)
    if (onFocus) {
      onFocus()
    }
  }

  const handleBlur = () => {
    setFocus(false)
    if (onBlur) {
      onBlur()
    }
  }

  return (
    <InlineBlock
      class="bui-show-keyboard-focus"
      component="button"
      height="60px"
      width="60px"
      padding="0"
      borderRadius="50%"
      background="none"
      border="none"
      position="relative"
      outline="none"
      userSelect="none"
      WebkitUserSelect="none"
      WebkitTapHighlightColor="rgba(0, 0, 0, 0)"
      color={color}
      cursor={clickable ? 'pointer' : undefined}
      props={{
        ref: iconButton,
        name,
        'aria-label': name,
        type: 'button',
        onClick: clickable ? handleClick : null,
        onKeyDown: clickable ? handleKeyDown : null,
        onMouseOver: clickable && supportsHover ? handleMouseOver : null,
        onMouseOut: clickable && supportsHover ? handleMouseOut : null,
        onFocus: clickable ? handleFocus : null,
        onBlur: clickable ? handleBlur : null,
        tabIndex: clickable ? 0 : -1
      }}
    >
      <Block
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        border="4px solid white"
        borderRadius="50%"
      />
      <Block
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Block
          borderRadius="50%"
          background="var(--error)"
          width={state === 'recording' ? '52px' : '40px'}
          height={state === 'recording' ? '52px' : '40px'}
          transition="width .18s cubic-bezier(0, 0, .2, 1), height .18s cubic-bezier(0, 0, .2, 1)"
          willChange="width, height"
        ></Block>
      </Block>
      <Block
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        display="flex"
        alignItems="center"
        justifyContent="center"
        transition="opacity .18s cubic-bezier(0, 0, .2, 1)"
        willChange="opacity"
        opacity={state === 'recording' ? 1 : 0}
      >
        <StopIcon fill="white" size={36} />
      </Block>
      {keyPressedHighlights}
      <Ripples borderRadius={borderRadius} color={highlightColor} disabled={!clickable} />
      {tooltipText && (
        <Tooltip visible={focus || hover} generator={iconButton} text={tooltipText} />
      )}
    </InlineBlock>
  )
}
